import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom'; 
import { useAuth } from '../context/AuthContext';
import { usePlanAccess } from '../hooks/usePlanAccess'; 

// 🛑 CORREÇÃO AQUI: Adicionar 'component' e 'children' nas props
const PrivateRoute = ({ requiredResource, requiredAction = 'can_access', component: Component, children }) => {
    const { isAuthenticated, user, loading, subscription, hasActiveSubscription } = useAuth();
    const { isModuleAvailable, getRequiredPlan, needsUpgrade } = usePlanAccess();
    const location = useLocation(); 

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                    <p className="text-emerald-600 font-semibold text-lg">Carregando...</p>
                </div>
            </div>
        );
    }

    // 1. CHECAGEM DE AUTENTICAÇÃO
    if (!isAuthenticated || !user) {
        return <Navigate to="/login" replace />; 
    }

    // 🛑 NOVO: Verificar se usuário está pendente de aceitação
    // Se estiver, redirecionar para página de perfil para aceitar o vínculo
    if (user?.status === 'pendente_aceitacao') {
        // Permitir acesso apenas à página de perfil para aceitar o vínculo
        if (location.pathname === '/profile') {
            // Permitir acesso ao perfil para aceitar o vínculo
        } else {
            return (
                <Navigate 
                    to="/profile" 
                    replace 
                    state={{ 
                        message: 'Você precisa aceitar o vínculo com a organização antes de acessar outras páginas.',
                        requires_acceptance: true
                    }} 
                />
            );
        }
    }
    
    // 2. CHECAGEM DE PLANO (antes de RBAC)
    // ✅ CORREÇÃO: Admins globais não precisam de assinatura
    const isGlobalAdmin = user && ['global_admin', 'super_admin', 'admin'].includes(user.role);
    
    // Se é admin global, pula verificação de assinatura
    if (!isGlobalAdmin) {
        // ✅ CORREÇÃO: Verificar se usuário tem assinatura ativa ou trial ativo
        // Se não tem organizacao_id mas tem trial ativo, o RBAC já deu as permissões
        if (user?.organizacao_id && subscription && !hasActiveSubscription()) {
            // Se não é trial ou trial expirado, e não é PENDENTE (aguardando webhook), redirecionar
            const isTrialActive = subscription?.is_trial && 
                                 (!subscription.data_expiracao || new Date(subscription.data_expiracao) >= new Date());
            const isPendingStripe = subscription.status === 'PENDENTE' && 
                                   subscription.stripe_subscription_id && 
                                   !subscription.stripe_subscription_id.startsWith('TRIAL_');
            
            if (!isTrialActive && !isPendingStripe) {
                return <Navigate to="/config/assinatura/sem-assinatura" replace />;
            }
        }
        
        // Se não tem organizacao_id e não tem trial ativo, verificar se precisa upgrade
        if (!user?.organizacao_id && requiredResource) {
            const hasTrialActive = subscription?.is_trial && hasActiveSubscription();
            if (!hasTrialActive && needsUpgrade(requiredResource)) {
                return (
                    <Navigate 
                        to="/config/assinatura/sem-assinatura" 
                        replace 
                        state={{ 
                            message: `Este módulo requer uma assinatura ativa.`,
                            module: requiredResource
                        }}
                    />
                );
            }
        }
    }

    // 3. CHECAGEM DE PERMISSÃO DE RECURSO (RBAC)
    // ✅ CORREÇÃO: Confiar nas permissões RBAC que já foram concedidas pelo middleware
    if (requiredResource) {
        // ✅ CORREÇÃO: Roles de alto privilégio (proprietário/admin) têm acesso total
        const isHighPrivilege = user?.role === 'proprietario' || user?.role === 'admin';
        if (isHighPrivilege) {
            // Proprietário/admin tem acesso total, pode prosseguir
        } else {
            const userPermissions = user.permissions || {};
            const resourcePerms = userPermissions[requiredResource];
            
            // Normalizar can_access (pode vir como boolean do backend)
            const normalizedValue = resourcePerms && (
                resourcePerms[requiredAction] === 1 || 
                resourcePerms[requiredAction] === true || 
                resourcePerms[requiredAction] === '1'
            );
            
            const hasAccess = normalizedValue === true;

            if (!hasAccess) {
                // ✅ CORREÇÃO: Encontrar primeiro módulo acessível e redirecionar automaticamente
                // Mapear módulos para rotas (ordem de prioridade)
                const moduleRoutes = {
                    'dashboard': '/dashboard',
                    'pacientes': '/pacientes',
                    'agendamentos': '/agendamentos',
                    'medicamentos': '/medicamentos',
                    'equipamentos': '/equipamentos',
                    'diario': '/diario',
                    'agenda_tarefas': '/tarefas',
                    'usuarios': '/usuarios',
                    'financeiro': '/financeiro',
                    'ponto': '/ponto',
                };

                // Ordem de prioridade para redirecionamento
                const priorityOrder = [
                    'dashboard',
                    'pacientes',
                    'agendamentos',
                    'medicamentos',
                    'equipamentos',
                    'diario',
                    'agenda_tarefas',
                    'usuarios',
                    'financeiro',
                    'ponto',
                ];

                // Encontrar primeiro módulo acessível (excluindo o recurso negado)
                let firstAccessibleRoute = null;
                
                for (const module of priorityOrder) {
                    if (module === requiredResource) continue; // Pular o módulo que foi negado
                    
                    const modulePerms = userPermissions[module];
                    const hasModuleAccess = modulePerms && (
                        modulePerms.can_access === 1 || 
                        modulePerms.can_access === true || 
                        modulePerms.can_access === '1'
                    );

                    if (hasModuleAccess && moduleRoutes[module]) {
                        firstAccessibleRoute = moduleRoutes[module];
                        break;
                    }
                }

                // Se encontrou um módulo acessível, redirecionar para ele
                if (firstAccessibleRoute) {
                    return (
                        <Navigate 
                            to={firstAccessibleRoute}
                            replace 
                        />
                    );
                }

                // Se não encontrou nenhum módulo acessível, redirecionar para perfil (sempre acessível)
                return (
                    <Navigate 
                        to="/profile"
                        replace 
                    />
                );
            }
        }
    }
    
    // 🛑 CORREÇÃO CHAVE: Se há children (usado para rotas aninhadas), renderize-os.
    if (children) {
        return children; 
    }

    // Se há um componente (usado para rotas simples), renderize-o.
    if (Component) {
        return <Component />;
    }

    // 3. Rota PAI (como o PrivateRoute que envolve DashboardLayout): Renderiza o Outlet
    // Esta é a última verificação.
    return <Outlet />;
};

export default PrivateRoute;